import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import ChartRenderer from '../components/ChartRenderer';
import FullscreenChartModal from '../components/FullscreenChartModal';
import RevolvingGlobe from '../components/RevolvingGlobe';
import YouTubeEmbed from '../components/YouTubeEmbed';
import { useProject } from '../hooks/useApi';
import { Project } from '../types';

const Services: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { project, loading } = useProject(parseInt(id || '0'));
  const [localProject, setLocalProject] = useState<Project | null>(null);
  const [selectedMetric, setSelectedMetric] = useState<any>(null);

  useEffect(() => {
    if (project) {
      setLocalProject(project);
    }
  }, [project]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-accent-blue"></div>
      </div>
    );
  }

  if (!localProject) {
    return (
      <div className="flex justify-center items-center h-screen">
        <p className="text-xl text-medium-gray">Project not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-black to-deep-navy py-8 px-4 sm:px-6 lg:px-8">
      {/* Navigation Bar */}
      <nav className="max-w-[95%] xl:max-w-[85%] mx-auto flex justify-between items-center mb-12">
        <div className="text-xl font-bold text-accent-blue">Procista AI</div>
        <div className="flex space-x-6">
          <Link 
            to="/" 
            className="text-medium-gray hover:text-accent-blue transition-colors duration-300 font-medium"
          >
            Home
          </Link>
          <Link 
            to="/about" 
            className="text-medium-gray hover:text-accent-blue transition-colors duration-300 font-medium"
          >
            About Us
          </Link>
          <button 
            onClick={() => window.open('https://calendly.com/waceem/initial-project-consultation', '_blank')}
            className="bg-gradient-to-r from-navy-blue to-accent-blue hover:from-accent-blue hover:to-navy-blue text-white font-semibold py-2 px-6 rounded-full transition-all duration-300 text-sm"
          >
            Contact
          </button>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-deep-navy/50 backdrop-blur-lg rounded-2xl border border-navy-blue p-6 md:p-8"
        >
          <div className="flex flex-col md:flex-row gap-8">
            <div className="md:w-1/2">
              <div className="aspect-video bg-dark-navy rounded-xl overflow-hidden flex items-center justify-center mb-6">
                {project.youtube_url ? (
                  <YouTubeEmbed 
                    videoId={project.youtube_url} 
                    title={project.title} 
                  />
                ) : (
                  <div className="text-medium-gray">Video placeholder</div>
                )}
              </div>
              
              <div className="bg-deep-navy/60 p-6 rounded-xl">
                <h2 className="text-2xl font-bold mb-4 text-light">{localProject.title}</h2>
                <p className="text-light mb-6">{localProject.full_description}</p>
                
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-gradient-to-br from-accent-blue/20 to-navy-blue/30 p-5 rounded-xl border border-accent-blue/30">
                    <h3 className="text-medium-gray text-base mb-2">Starting Price</h3>
                    <p className="text-3xl font-bold text-white">From ${localProject.price_start}<span className="text-xl font-normal">/mo</span></p>
                  </div>
                  <div className="bg-navy-blue/50 p-4 rounded-lg">
                    <h3 className="text-medium-gray text-sm">ROI Summary</h3>
                    <p className="text-xl font-semibold text-light">{localProject.roi_summary}</p>
                  </div>
                  <div className="bg-navy-blue/50 p-4 rounded-lg">
                    <h3 className="text-medium-gray text-sm">Implementation Time</h3>
                    <p className="text-xl font-semibold text-light">{localProject.time_to_implement}</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="md:w-1/2">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-light">Project Metrics</h3>
              </div>
              
              <div className="space-y-8">
                {localProject.metrics.map(metric => (
                  <div key={metric.id} className="bg-deep-navy/60 p-6 rounded-xl relative">
                    <div className="flex justify-between items-start mb-4">
                      <h4 className="text-xl font-semibold text-light">{metric.metric_name}</h4>
                      <button 
                        onClick={() => setSelectedMetric(metric)}
                        className="text-sm bg-accent-blue hover:bg-blue-700 text-white py-1 px-3 rounded transition-colors"
                      >
                        View Fullscreen
                      </button>
                    </div>
                    
                    <div className="mb-3 flex gap-4 text-sm">
                      <div className="bg-dark-navy px-3 py-1 rounded">
                        <span className="text-medium-gray">X-axis: </span>
                        <span className="text-light">{metric.x_label}</span>
                      </div>
                      <div className="bg-dark-navy px-3 py-1 rounded">
                        <span className="text-medium-gray">Y-axis: </span>
                        <span className="text-light">{metric.y_label}</span>
                      </div>
                    </div>
                    
                    <ChartRenderer metric={metric} />
                  </div>
                ))}
              </div>
              
              <div className="mt-8 bg-gradient-to-r from-navy-blue/40 to-deep-navy/40 p-6 rounded-xl border border-navy-blue">
                <h3 className="text-xl font-bold mb-4 text-light">Book Your Demo</h3>
                <div className="space-y-4">
                  <button
                    onClick={() => window.open('https://calendly.com/waceem/initial-project-consultation', '_blank')}
                    className="w-full bg-gradient-to-r from-navy-blue to-accent-blue hover:from-accent-blue hover:to-navy-blue text-white font-semibold py-3 px-8 rounded-lg transition-all duration-300"
                  >
                    Schedule Free Demo
                  </button>
                  
                  <p className="text-center text-medium-gray text-sm">
                    Click to schedule your personalized demo session
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
      
      {selectedMetric && (
        <FullscreenChartModal 
          metric={selectedMetric} 
          onClose={() => setSelectedMetric(null)} 
        />
      )}
      
      {/* Revolving Globe */}
      <RevolvingGlobe />
    </div>
  );
};

export default Services;